/**
 * GridTaskPanel Component
 * 
 * Right panel for grid-based task creation in the Eco Assets page.
 * Allows users to:
 * - Toggle between Trees and Grids view on the map
 * - Select grids visually on the map
 * - Configure and create tasks for selected grids
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  GridOn,
  Park,
  SelectAll,
  ClearAll,
  NearMe,
  Layers,
  Add
} from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import BASE_URL from '../../config';
import { formatInterval } from '../../utils/formatters';
import { useOrg } from '../../contexts/OrgContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { toast } from 'react-toastify';

const GridTaskPanel = ({
  siteId,
  subSiteId,  // Optional sub-site ID - when set, use sub-site API endpoints
  siteName,
  viewMode,
  onViewModeChange,
  selectedGridIds,
  onSelectAllGrids,
  onClearSelection,
  onSelectGridsWithTrees,  // Select grids with tree associations from map
  totalGridCount,
  gridsWithTreesCount = 0, // Count of grids that have trees
  onCreateTasks,
  loading = false,
  // New props for tree-based grid selection
  selectedTreeIds = [],      // Tree IDs selected in TreeList
  selectedTreesData = [],    // Full tree data from TreeList
  selectedRunId = null,      // Current run ID for context
  selectedHealthFilter = 'All', // Health filter from TreeList
  hideViewToggle = false     // Hide the view toggle (when embedded in work orders panel)
}) => {
  const { selectedOrgId } = useOrg();
  const { can } = usePermissions();
  const canCreateTasks = can('Task Management', 'taskAssignment', 'create');

  // Task form state
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [formData, setFormData] = useState({
    workflow_template_id: '',
    contract_id: '',
    priority: 'medium',
    description: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // State for tree-based grid selection
  const [gridsFromTrees, setGridsFromTrees] = useState([]);
  const [treeGridsLoading, setTreeGridsLoading] = useState(false);
  
  // State for tree count preview (filter-aware)
  const [treeCountPreview, setTreeCountPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Map health filter names to database values
  const healthFilterMap = {
    'All': null,
    'Risk': 'low',
    'Average': 'medium',
    'Good': 'high'
  };

  // Fetch tree count preview when grids are selected
  useEffect(() => {
    const fetchTreeCountPreview = async () => {
      if (selectedGridIds.length === 0) {
        setTreeCountPreview(null);
        return;
      }
      
      setPreviewLoading(true);
      try {
        const user = getAuth().currentUser;
        const token = await user.getIdToken();
        
        const healthValue = healthFilterMap[selectedHealthFilter];
        
        const response = await fetch(`${BASE_URL}/api/grids/tree-count-preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            grid_ids: selectedGridIds,
            health_filter: healthValue,
            run_id: selectedRunId || null
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setTreeCountPreview(data);
        }
      } catch (error) {
        console.error('Error fetching tree count preview:', error);
      } finally {
        setPreviewLoading(false);
      }
    };
    
    fetchTreeCountPreview();
  }, [selectedGridIds, selectedHealthFilter, selectedRunId]);

  // Fetch form options when panel mounts or site changes
  useEffect(() => {
    if (siteId && selectedOrgId) {
      fetchFormOptions();
    }
  }, [siteId, selectedOrgId]);

  // Fetch grids containing selected trees
  const handleSelectGridsFromTrees = async () => {
    if (!selectedTreeIds || selectedTreeIds.length === 0) {
      toast.info('No trees selected. Open Tree List to select trees first.', { position: 'top-center' });
      return;
    }

    setTreeGridsLoading(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const endpoint = subSiteId
        ? `${BASE_URL}/api/sub-sites/${subSiteId}/grids/by-trees`
        : `${BASE_URL}/api/sites/${siteId}/grids/by-trees`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tree_ids: selectedTreeIds,
          include_tree_data: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch grids');
      }

      const data = await response.json();
      setGridsFromTrees(data.grids);
      
      // Auto-select these grids on the map
      if (onSelectGridsWithTrees && data.grids.length > 0) {
        const gridIds = data.grids.map(g => g.id);
        onSelectGridsWithTrees(gridIds);
        toast.success(`Found ${data.total_grids} grids containing ${data.total_trees} selected trees`, { 
          position: 'top-center' 
        });
      } else if (data.grids.length === 0) {
        toast.info('No grids found for selected trees', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error fetching grids from trees:', error);
      toast.error('Failed to find grids for selected trees', { position: 'top-center' });
    } finally {
      setTreeGridsLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    setFormLoading(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [templatesRes, contractsRes] = await Promise.all([
        fetch(`${BASE_URL}/api/workflow-templates?organisation_id=${selectedOrgId}&asset_type_id=1`, { headers }),
        fetch(`${BASE_URL}/api/contractdropdown?organisation_id=${selectedOrgId}`, { headers }),
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setWorkflowTemplates(data);
        // Default to first available template (prefer ones named "Maintenance")
        const maintenance = data.find(t => t.name?.toLowerCase().includes('maintenance'));
        if (maintenance) setFormData(prev => ({ ...prev, workflow_template_id: maintenance.id }));
        else if (data.length > 0) setFormData(prev => ({ ...prev, workflow_template_id: data[0].id }));
      }
      if (contractsRes.ok) {
        const data = await contractsRes.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Error fetching form options:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedGridIds.length === 0) {
      toast.warning('Please select at least one grid on the map', { position: 'top-center' });
      return;
    }

    if (!formData.workflow_template_id) {
      toast.warning('Please select a workflow template', { position: 'top-center' });
      return;
    }

    setSubmitting(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      // Prepare tree_data for the task (grouped by grid)
      // Each tree includes grid_id for proper storage
      let treeDataForTask = null;
      if (gridsFromTrees.length > 0) {
        // Use the tree data we fetched when selecting grids from trees
        treeDataForTask = gridsFromTrees.flatMap(grid => 
          (grid.trees || []).map(tree => ({
            ...tree,
            grid_id: grid.id
          }))
        );
      } else if (selectedTreesData && selectedTreesData.length > 0) {
        // Use trees from TreeList selection
        treeDataForTask = selectedTreesData.map(tree => ({
          id: tree.id,
          health: tree.health ?? 'N/A',
          water_stress: tree.water_stress ?? 'N/A',
          species: tree.species ?? 'N/A',
          centroid: tree.centroid ?? null,
          grid_id: tree.grid_id ?? null
        }));
      }

      const response = await fetch(`${BASE_URL}/api/tasks/grid-based`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          organisation_id: selectedOrgId,
          site_id: siteId,
          sub_site_id: subSiteId || undefined,
          grid_ids: selectedGridIds,
          workflow_template_id: formData.workflow_template_id,
          contract_id: formData.contract_id || undefined,
          priority: formData.priority,
          description: formData.description || `Grid tasks for ${siteName}`,
          run_id: selectedRunId || null,
          health_filter: healthFilterMap[selectedHealthFilter],
          tree_data: treeDataForTask
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create tasks');
      }

      const result = await response.json();
      const treeMsg = result.total_trees > 0 ? ` with ${result.total_trees} trees` : '';
      const filterMsg = selectedHealthFilter !== 'All' ? ` (${selectedHealthFilter} filter)` : '';
      toast.success(`✅ Created ${result.task_count} tasks${treeMsg}${filterMsg}!`, { position: 'top-center' });
      
      // Clear selection and notify parent
      onClearSelection();
      setGridsFromTrees([]); // Clear cached grids from trees
      if (onCreateTasks) {
        onCreateTasks(result);
      }

      // Reset form
      setFormData(prev => ({
        workflow_template_id: prev.workflow_template_id, // keep selected workflow
        contract_id: '',
        priority: 'medium',
        description: ''
      }));

    } catch (error) {
      console.error('Error creating tasks:', error);
      toast.error(error.message || 'Failed to create tasks', { position: 'top-center' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* View Mode Toggle - Hidden when embedded */}
      {!hideViewToggle && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              VIEW MODE
            </Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && onViewModeChange(newMode)}
              fullWidth
              size="small"
            >
              <ToggleButton value="trees" sx={{ textTransform: 'none' }}>
                <Park sx={{ mr: 0.5, fontSize: 18 }} />
                Trees
              </ToggleButton>
              <ToggleButton value="grids" sx={{ textTransform: 'none' }}>
                <GridOn sx={{ mr: 0.5, fontSize: 18 }} />
                Grids
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider sx={{ my: 1 }} />
        </>
      )}

      {/* Grid Selection Section - Only visible in grid mode */}
      {viewMode === 'grids' && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              GRID SELECTION
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading grids...</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip 
                    label={`${selectedGridIds.length} of ${totalGridCount} grids selected`}
                    color={selectedGridIds.length > 0 ? 'primary' : 'default'}
                    size="small"
                  />
                  {gridsWithTreesCount > 0 && (
                    <Chip 
                      label={`${gridsWithTreesCount} with trees`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Tooltip title="Select all grids">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SelectAll />}
                      onClick={onSelectAllGrids}
                      disabled={totalGridCount === 0}
                    >
                      All
                    </Button>
                  </Tooltip>
                  <Tooltip title="Select only grids containing trees">
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<Park />}
                      onClick={onSelectGridsWithTrees}
                      disabled={gridsWithTreesCount === 0}
                    >
                      With Trees
                    </Button>
                  </Tooltip>
                  <Tooltip title="Clear selection">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ClearAll />}
                      onClick={onClearSelection}
                      disabled={selectedGridIds.length === 0}
                    >
                      Clear
                    </Button>
                  </Tooltip>
                </Box>

                {/* Tree-based Grid Selection */}
                {selectedTreeIds.length > 0 && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 1.5, 
                    bgcolor: 'success.50', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'success.200'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                        🌳 {selectedTreeIds.length} trees selected
                      </Typography>
                      {selectedHealthFilter !== 'All' && (
                        <Chip 
                          label={selectedHealthFilter} 
                          size="small" 
                          color={selectedHealthFilter === 'Risk' ? 'error' : selectedHealthFilter === 'Average' ? 'warning' : 'success'}
                        />
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={treeGridsLoading ? <CircularProgress size={16} color="inherit" /> : <NearMe />}
                      onClick={handleSelectGridsFromTrees}
                      disabled={treeGridsLoading}
                      fullWidth
                    >
                      {treeGridsLoading ? 'Finding Grids...' : 'Select Grids for These Trees'}
                    </Button>
                    {gridsFromTrees.length > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'success.dark' }}>
                        Found {gridsFromTrees.length} grids with {gridsFromTrees.reduce((sum, g) => sum + (g.tree_count || 0), 0)} trees
                      </Typography>
                    )}
                  </Box>
                )}

                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                  💡 Tip: Hold Ctrl/Cmd and click grids on map to multi-select
                </Typography>

                {totalGridCount === 0 && siteId && (
                  <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    No grids found. Generate grids from Sites page.
                  </Alert>
                )}

                {!siteId && (
                  <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    Select a site to view grids.
                  </Alert>
                )}
              </>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          {!canCreateTasks && (
            <Alert severity="info" sx={{ mt: 1 }}>
              You don't have permission to create tasks.
            </Alert>
          )}

          {canCreateTasks && (
          <>
          {/* Task Creation Form */}
          <Box sx={{ mb: 2, flexGrow: 1, overflow: 'auto' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              TASK DETAILS
            </Typography>

            {formLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Workflow *</InputLabel>
                  <Select
                    value={formData.workflow_template_id}
                    label="Workflow *"
                    onChange={(e) => setFormData({ ...formData, workflow_template_id: e.target.value })}
                  >
                    {workflowTemplates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Contract (optional)</InputLabel>
                  <Select
                    value={formData.contract_id}
                    label="Contract (optional)"
                    onChange={(e) => setFormData({ ...formData, contract_id: e.target.value })}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {contracts.map((contract) => (
                      <MenuItem key={contract.id} value={contract.id}>
                        {contract.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Create Tasks Button */}
          <Box sx={{ mt: 'auto', pt: 1 }}>
            {/* Tree Count Preview */}
            {selectedGridIds.length > 0 && (
              <Box sx={{ 
                mb: 1.5, 
                p: 1.5, 
                bgcolor: selectedHealthFilter !== 'All' ? 'warning.50' : 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: selectedHealthFilter !== 'All' ? 'warning.200' : 'grey.200'
              }}>
                {previewLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Counting trees...</Typography>
                  </Box>
                ) : treeCountPreview ? (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      🌳 {treeCountPreview.total_trees} trees will be tagged
                    </Typography>
                    {selectedHealthFilter !== 'All' && (
                      <Chip 
                        label={`${selectedHealthFilter} Filter Active`}
                        size="small"
                        color={selectedHealthFilter === 'Risk' ? 'error' : selectedHealthFilter === 'Average' ? 'warning' : 'success'}
                        sx={{ mr: 0.5 }}
                      />
                    )}
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                      Across {selectedGridIds.length} selected grid{selectedGridIds.length !== 1 ? 's' : ''}
                    </Typography>
                  </>
                ) : null}
              </Box>
            )}
            
            <Button
              variant="contained"
              fullWidth
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Add />}
              onClick={handleSubmit}
              disabled={selectedGridIds.length === 0 || submitting || !formData.workflow_template_id}
              sx={{ 
                py: 1.5,
                fontWeight: 600,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              {submitting ? 'Creating...' : `Create ${selectedGridIds.length} Task${selectedGridIds.length !== 1 ? 's' : ''}`}
            </Button>
            
            {selectedGridIds.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary' }}>
                One task will be created per selected grid
              </Typography>
            )}
          </Box>
          </>
          )}
        </>
      )}

      {/* Trees Mode - Show helper text (only when view toggle is visible) */}
      {viewMode === 'trees' && !hideViewToggle && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <Park sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
          <Typography variant="body2">
            Viewing individual trees on the map.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Switch to <strong>Grids</strong> mode to create tasks for grid areas.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GridTaskPanel;
